import { useState } from 'react';
import { Select, TextInput } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { OTHER } from '../../constants/iranGeo';

/**
 * A place field: a picker over known names, or a text box once the answer is
 * «موردی دیگر».
 *
 * Extracted from «کلینیک من» so the registration wizard and the clinic profile
 * ask for a province the same way. They did not: the wizard used bare
 * `<select>`s over `provinceOptions`, the profile used this component, and the
 * two disagreed about what happens when a clinic is somewhere the list has
 * never heard of. A clinic registered in a village could not be registered at
 * all; a clinic edited afterwards could.
 *
 * The hazard this component exists for belongs to the PROFILE rather than the
 * wizard, and it is worth restating because it is what forbids simplifying it:
 * `updateClinic` is a FULL REPLACE, so a `<select>` whose options do not
 * contain the clinic's stored province renders empty and the next save writes
 * that emptiness over a real value. `provinceOptions` / `cityOptions`
 * therefore carry an unknown stored value INTO the list, and «موردی دیگر»
 * switches the field back to free text. A clinic saved before the pickers
 * shipped keeps its province.
 *
 * `manual` is component state and not draft state on purpose — which control
 * you are looking at is a fact about this render, where the value is a fact
 * about the clinic. Starting in manual mode for a stored value that is not in
 * the list would fight the option `provinceOptions` already prepends.
 */

/** The value that turns a picker back into a text box. */
export const OTHER_OPTION = (t) => ({ value: OTHER, label: t('clinic.fields.otherPlace') });

export default function PlaceField({
  label,
  value,
  options,
  onChange,
  disabled,
  placeholder,
  helperText,
  error,
  testId,
  fieldRef,
}) {
  const { t } = useTranslation();
  const [manual, setManual] = useState(false);

  return (
    <div ref={fieldRef} data-testid={testId}>
      {manual ? (
        <TextInput
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          helperText={helperText}
          error={error}
        />
      ) : (
        <Select
          label={label}
          value={value || ''}
          options={[...options, OTHER_OPTION(t)]}
          placeholder={placeholder}
          disabled={disabled}
          helperText={helperText}
          error={error}
          onChange={(e) => {
            const next = e.target.value;
            if (next === OTHER) { setManual(true); onChange(''); return; }
            onChange(next);
          }}
        />
      )}
    </div>
  );
}
